/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/internal/Observable';
import {
  AceptaCobroAutomaticoResponse,
  CreditData,
  DatosBancarios,
  DatosEmpleo,
  DatosEstadoCivil,
  DatosPrestamo,
  DatosProvincias,
  PrestamosCalculosResponse,
  User,
} from '../models/credit-simulator-interface';
import { AgilcredBaseService } from './agilcred-base.service';
import { ClientExistsResponse } from '../models/response/client-exists.response';
import { SendVerificationCodeResponse } from '../models/response/send-verification-code.response';
import { HttpParams } from '@angular/common/http';
import { environment } from '../enviroments/enviroment';
import { LaundryPreventionResponse } from '../models/response/laundry-prevention.response';
import { OnboardingStartResponse } from '../models/response/onboarding-start.response';
import { DataService } from './data.service';
import { of, map, mergeMap, take, tap } from 'rxjs';
import { ClientLoginResponse } from '../models/response/client-login';
import { ClientInfoResponse } from '../models/response/client-info';
import { LoginResponse } from '../models/response/login.response';
import { UserLineaIdResponse } from '../models/response/user-linea-id.response';
import { BankAccount } from '../models/response/account.response';
import { HasActiveRequestResponse } from '../models/response/has-active-request.response';

@Injectable({
  providedIn: 'root',
})
export class CreditService extends AgilcredBaseService {
  constructor(private dataService: DataService) {
    super();
  }

  checkActiveRequest(): Observable<HasActiveRequestResponse> {
    return this.get('solicitud/check-request');
  }

  getUserLineaId(): Observable<UserLineaIdResponse> {
    const usuario = this.dataService.validatedDocument() ?? '';
    const params = new HttpParams({ fromObject: { usuario } });
    return this.get('get-user-linea-id', params).pipe(map((response) => response.data));
  }

  aceptaCobroAutomatico(aceptaCobroAutomatico: boolean): Observable<AceptaCobroAutomaticoResponse> {
    return this.post('solicitud/acepta-cobro-automatico', { acepta_cobro_automatico: aceptaCobroAutomatico });
  }

  getLoanCalculations(lineaId?: number): Observable<PrestamosCalculosResponse> {
    const params = new HttpParams({ fromObject: { lineaId: lineaId ?? '' } });
    return this.get('prestamos-calculos', lineaId ? params : undefined);
  }

  checkIfClientExists(usuario: string): Observable<ClientExistsResponse> {
    const params = new HttpParams({ fromObject: { usuario } });
    return this.http.get<ClientExistsResponse>(`${environment.apiUrl}/existe-cliente`, { params });
  }

  loginClient(usuario: string, contrasena: string): Observable<ClientLoginResponse> {
    const params = new HttpParams({ fromObject: { usuario, contrasena } });
    return this.http.post<ClientLoginResponse>(`${environment.apiUrl}/login`, params);
  }

  getClientInfo(): Observable<ClientInfoResponse> {
    return this.get(`me`);
  }

  getProvincias(): Observable<DatosProvincias> {
    return this.get('formularios/provincias');
  }

  getMaritalStatus(): Observable<DatosEstadoCivil> {
    return this.get('formularios/estados-civiles');
  }

  getLoanPurposes(): Observable<DatosPrestamo> {
    return this.dataService.getUserObservable().pipe(
      mergeMap(({ usuario }) => {
        const params = new HttpParams({ fromObject: { usuario } });
        return this.get('formularios/motivos-prestamos', params);
      }),
    );
  }

  getJobs(): Observable<DatosEmpleo> {
    return this.dataService.getUserObservable().pipe(
      mergeMap(({ usuario }) => {
        const params = new HttpParams({ fromObject: { usuario } });
        return this.get('formularios/empleos', params);
      }),
    );
  }

  registerNewUser(user: User): Observable<LoginResponse> {
    return this.post<LoginResponse>('registro', user);
  }

  updatePhone(phone: string): Observable<any> {
    return this.put('celular', { value: phone });
  }

  sendVerificationCodeToUserPhone(): Observable<SendVerificationCodeResponse> {
    const dni = this.dataService.validatedDocument() ?? '';
    return this.post<SendVerificationCodeResponse>('verification-code/create', { dni, type: 'EMAIL' }).pipe(
      tap((response) => {
        if (response.status === 'ok') {
          this.dataService.setCodeData(response);
        }
      }),
    );
  }

  validateVerificationCodeAndEnableUser(code: number): Observable<any> {
    return this.dataService.getCodeData$().pipe(
      take(1),
      mergeMap((codeData) =>
        this.post('verification-code/validate', { uuid: codeData?.data?.uuid ?? '', code: String(code) }),
      ),
    );
  }

  requestCreation(creditData: CreditData): Observable<any> {
    return this.dataService
      .getGiveawayLink$()
      .pipe(mergeMap((link) => this.post('solicitud/crear', { ...creditData, link: link ?? undefined })));
  }

  //solicitud datos bancarios
  requestDataBank(bankData: DatosBancarios): Observable<any> {
    return this.post(`solicitud/datos-bancarios`, bankData);
  }

  // inicializa onBoarding widget
  startOnBoardingProvider(): Observable<OnboardingStartResponse> {
    return this.get<OnboardingStartResponse>('onboarding/widget/start');
  }

  //finaliza onboarding widget
  endOnBoardingProvider(id: string): Observable<any> {
    return this.post(`onboarding/widget/end?id=${id}`, '');
  }

  updatePaymentCard(paymentCardId: number): Observable<any> {
    return this.put('solicitud/add-payment-card', { payment_card_id: paymentCardId });
  }

  willProvidePaymentCard(): Observable<any> {
    return this.put('solicitud/will-provide-payment-card', null);
  }

  //Genera datos para iniciar el Device Data Collector de Kount.
  startRiskFraudProvider(): Observable<{ data: { session_id: string; client_id: string } }> {
    return of({ data: { session_id: 'none', client_id: 'none' } });
    //return this.get('risk/fraud/start');
  }

  //Recibe un dictamen en función al resultado de risk score: aprobado = "approved" rechazado = "rejected"
  endRiskFraudProvider(session_id: any): Observable<{ result: 'approved' | 'rejected'; session_id: string }> {
    return of({ result: 'approved', session_id });
    //return this.post(`risk/fraud/end?session_id=${session_id}`, '');
  }

  //Prevención de lavado
  laundryPrevention(): Observable<LaundryPreventionResponse> {
    return this.post(`risk/prevention`, null);
  }

  requestDuplication(creditData: Omit<CreditData, 'acepta_terminos_condiciones'>): Observable<any> {
    return this.post('solicitud/actualizar', creditData);
  }

  public loadAccounts(): Observable<BankAccount[]> {
    return this.get('solicitud/accounts').pipe(map(({ accounts }) => accounts));
  }

  cancelSolicitud(solicitudId: number): Observable<any> {
    return this.patch('solicitud/cancel', { solicitud_id: solicitudId });
  }
}
